import _ from 'lodash';
import { defineMessages } from 'react-intl';

import ChartOption from '../../models/ChartOption';
import DataGroupingOption from '../../models/DataGroupingOption';
import DataOption from '../../models/DataOption';
import StatMessages from '../../StatMessages';
import getHoursOffset from '../../../utils/getHoursOffset';

export default class CallQueueReportPercent {
  constructor(intl) {
    if (!intl) {
      throw Error('Missing intl param');
    }

    const messages = defineMessages({
      AGGREGATED: {
        id: 'AGGREGATED',
        defaultMessage: 'Aggregate',
      },
      PER_AGENT: {
        id: 'PER_AGENT',
        defaultMessage: 'Per Agent',
      },
      PER_QUEUE: {
        id: 'PER_QUEUE',
        defaultMessage: 'Per Queue',
      },
    });

    /**
     * Displays chart options to the user
     * type: timeRange displays an input and sets the start/end for the api request
     * type: queueFilter displays an input and sets the queue/s for the api request
     * @type {ChartOption[]}
     */
    this.chartOptions = [
      new ChartOption({ type: 'timeRange', required: true }),
      new ChartOption({ type: 'queueFilter', required: false }),
    ];

    /**
     * Displayed as "Breakdown" select options to the user.
     * When a user selects the grouping option it will set the data adapter and display filter options
     * @param intl
     * @returns {DataGroupingOption[][]}
     */
    const categoryDefs = [
      new DataGroupingOption({
        label: intl.formatMessage({ ...messages.AGGREGATED }),
        category: 'AGGREGATED',
        apiParams: {
          type: 'domain',
        },
        adapter: 'callQueueSumPercentToLineAdapter',
        filterObjects: ['queue', 'group', 'site'],
      }),
      new DataGroupingOption({
        label: intl.formatMessage({ ...messages.PER_AGENT }),
        category: 'PER_AGENT',
        apiParams: {
          type: 'op_term_sub',
        },
        adapter: 'callQueuePercentToLineAdapter',
        filterObjects: ['subscriber'],
      }),
      new DataGroupingOption({
        label: intl.formatMessage({ ...messages.PER_QUEUE }),
        category: 'PER_QUEUE',
        apiParams: {
          type: 'queue_name',
        },
        adapter: 'callQueuePercentToLineAdapter',
        filterObjects: ['callqueue', 'group'],
      }),
    ];
    const categoryDefsNoAgent = categoryDefs.filter((option) => option.category === 'AGGREGATED'
      || option.category === 'PER_QUEUE');

    const hourOffset = getHoursOffset();

    /**
     * Default callqueuereport api params
     * @type {{object: string, action: string, format: string, range_interval: string, value_offset: string}}
     */
    const apiParams = {
      object: 'callqueuereport',
      action: 'read',
      format: 'json',
      range_interval: hourOffset,
      value_offset: hourOffset,
      unit: 'seconds',
    };

    const statMessages = new StatMessages(intl);

    /**
     * add socket subscriptions and events if any
     * Example Props
     * stat: 'AR'
     * socketSubscriptions: [{ type: 'agent', domain: string, user: string, filter: string }]
     * socketEvents: [{ name: string, adapter: function, adapterParams: object }] }
     * @type {*[]}
     */
    const stats = [
      { stat: 'AR', ...statMessages.messages.AR, categoryDefs: categoryDefsNoAgent },
      { stat: 'SL', ...statMessages.messages.SL, categoryDefs: categoryDefsNoAgent },
    ];

    /**
     * @type {DataOption[]}
     */
    const options = stats.map((stat) => new DataOption({
      type: 'api',
      apiParams: { ...apiParams, stat: stat.stat },
      categoryDefs: stat.categoryDefs ? stat.categoryDefs : categoryDefs,
      hasCategories: true,
      hasColumns: false,
      supportedCharts: ['ResponsiveLine'],
      xAxis: 'datetime',
      ...stat,
    }));

    this.dataOptions = _.keyBy(options, 'stat');
  }
}
